import React, { useEffect, useState } from 'react';
import Modal from 'components/elements/Modal';
import * as yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from 'components/elements/Form/Input/input';
import UsersIcon from 'assets/Icons/sidebarIcons/UsersIcon';
import { useQuery } from 'react-query';
import { AdminProps } from 'services/hooks/admin-service';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import useSaveAdmin from './crud/useSave';
import SelectInput from 'components/elements/Form/select/selectInput';
import AccountService, { AccountProps } from 'services/hooks/account-service';
import PrimaryButton from 'components/elements/Form/button/primaryButton';
import { BuildingProps } from 'services/hooks/building-service';
import { toast } from 'react-toastify';
import {
  generateApiFailErrorMessage,
  generateSaveErrorMessage,
  generateSaveSuccessMessage,
  generateUpdateErrorMessage,
  generateUpdateSuccessMessage
} from 'utils/helpers';
import { GetNonServerErrorMessage, defaultUUIDValue, AdminRoleOptions } from 'utils/constants';
import AdminRoleFields from './AdminRoleFields';
import { useAuth } from 'services/auth-service';

export type SaveProps = {
  entityName: string;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setEditUUID?: React.Dispatch<React.SetStateAction<string>>;
  createQuery?: (newAdmin: AdminProps) => Promise<AdminProps>;
  updateQuery?: (uuid: string | undefined, updatedAdmin: AdminProps) => Promise<AdminProps>;
  editQuery?: (uuid: string | undefined) => Promise<AdminProps>;
  editUUID?: string;
  queryName: string;
};

/* The code is defining a validation schema using the `yup` library. If these validation rules are not met, an error message will be displayed. */
const CreateAdminSchema = yup.object().shape({
  username: yup.string().email('Invalid email address').required('Username is required'),
  password: yup
    .string()
    .required('New password is required')
    .min(8, 'Must be at least 8 characters')
    .matches(/[A-Z]/, 'Must include at least one uppercase letter')
    .matches(/[a-z]/, 'Must include at least one lowercase letter')
    .matches(/[0-9]/, 'Must include at least one number'),
  role: yup.string().required('Role is Required'),
  accountUuid: yup.string().when('role', {
    is: (role: string) => role === 'ACCOUNT' || role === 'BUILDING' || role === 'ORG',
    then: yup.string().required('Account is required'),
    otherwise: yup.string().notRequired()
  }),
  buildingUuid: yup.string().when('role', {
    is: (role: string) => role === 'BUILDING' || role === 'ORG',
    then: yup.string().required('Building is required'),
    otherwise: yup.string().notRequired()
  }),
  organizationUuid: yup.string().when('role', {
    is: (role: string) => role === 'ORG',
    then: yup.string().required('Organization is required'),
    otherwise: yup.string().notRequired()
  })
});

export default function Save({
  entityName,
  showModal,
  setEditUUID,
  setShowModal,
  createQuery,
  updateQuery,
  editQuery,
  editUUID,
  queryName
}: SaveProps) {
  const {
    state: { user }
  } = useAuth();
  const [getAdminResult, setGetAdminResult] = useState<AdminProps | null>(null);
  const [disabled, setDisabled] = useState(false);

  const { isSuccess, data } = useQuery<AdminProps | undefined, Error>(
    'admin',
    () => (editUUID ? editQuery?.(editUUID) : undefined),
    {
      enabled: editUUID ? true : false,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Admin'));
      }
    }
  );

  /* The `useForm` hook from the `react-hook-form` library is being used to create a form with form validation. */
  const methods = useForm({
    defaultValues: getAdminResult
      ? {
          uuid: getAdminResult?.uuid,
          username: getAdminResult?.username,
          password: getAdminResult?.password,
          accountUuid: getAdminResult?.accountUuid,
          buildingUuid: getAdminResult?.buildingUuid,
          organizationUuid: getAdminResult?.organizationUuid,
          role: getAdminResult?.role
        }
      : {
          uuid: '',
          username: '',
          password: '',
          accountUuid: '',
          buildingUuid: '',
          organizationUuid: '',
          role: ''
        },
    resolver: yupResolver(CreateAdminSchema)
  });

  useEffect(() => {
    if (data && editUUID) {
      const cleanedAdminData = {
        ...data,
        accountUuid: data.accountUuid !== defaultUUIDValue ? data.accountUuid : '',
        buildingUuid: data.buildingUuid !== defaultUUIDValue ? data.buildingUuid : '',
        organizationUuid: data.organizationUuid !== defaultUUIDValue ? data.organizationUuid : ''
      };
      setGetAdminResult(cleanedAdminData);
      /* It is being used to set the initial values of the form fields to the values retrieved from the `data` object. The `data` object contains 
      the Admin details fetched from the API when editing an existing account. By calling `methods.reset()` with the desired initial values, 
      the form fields will be populated with the fetched data when the form is rendered. */
      methods.reset(cleanedAdminData);
    }
  }, [data, editUUID, methods]);

  useEffect(() => {
    if (user.role === 'ACCOUNT') {
      methods.setValue('accountUuid', user.uuid);
    }
  }, [user.role, user.uuid, methods]);

  const [buildingOptions, setbuildingOptions] = useState<{ value: string; label: string }[]>([]);
  const [orgOptions, setOrgOptions] = useState<{ value: string; label: string }[]>([]);
  const [getAccountByIdResult, setGetAccountByIdResult] = useState<AccountProps | undefined>(
    undefined
  );

  /* The `watch` method of the `methods` object is likely used to subscribe to changes in the specified values and return the current value. */
  const accountWatch: string | undefined = methods.watch('accountUuid');
  const orgWatch: string | undefined = methods.watch('organizationUuid');
  const buildingWatch: string | undefined = methods.watch('buildingUuid');

  /* The function "formatedOptions" takes in a value and label as parameters and returns an object with the value and label properties. */
  const formatedOptions = (value: string, label: string) => {
    return { value: value, label: label };
  };

  const { data: accountData, refetch } = useQuery<AccountProps, Error>(
    'account',
    () => AccountService.findAccountById(accountWatch),
    {
      enabled: !!accountWatch,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Account'));
      }
    }
  );

  // Now, set the accountData in useState() variable
  useEffect(() => {
    if (accountData && accountWatch) {
      refetch();
      setGetAccountByIdResult(accountData);
    }
  }, [accountWatch, accountData, refetch]);

  /* The below code is used to populate options for buildings and organizations based on the result of a getAccountByIdResult API call. */
  useEffect(() => {
    if (getAccountByIdResult) {
      /* The below code is populating the building options for a form. It is mapping over an array of buildings obtained from the 
      `getAccountByIdResult` variable and creating formatted options for each building. */
      setbuildingOptions(
        (getAccountByIdResult?.buildings || []).map((building: BuildingProps) =>
          formatedOptions(building.uuid || '', building.name || '')
        )
      );

      // The below code is populating the Organization options for a form.
      if (buildingWatch) {
        const populateSelectedOrg: { value: string; label: string }[] = [];
        getAccountByIdResult?.buildings?.map(
          (building: BuildingProps) =>
            building.uuid === buildingWatch &&
            building?.organizations?.map((org: { uuid: string; name: string }) =>
              populateSelectedOrg.push(formatedOptions(org.uuid, org.name))
            )
        );
        setOrgOptions(populateSelectedOrg);
      } else {
        const populateOrg: { value: string; label: string }[] = [];
        getAccountByIdResult?.buildings?.forEach((building: BuildingProps) =>
          building?.organizations?.map((org: { uuid: string; name: string }) =>
            populateOrg.push(formatedOptions(org.uuid, org.name))
          )
        );
        setOrgOptions(populateOrg);
      }

      // If Organization is already selected then pre-populate the building associated with organization
      if (orgWatch) {
        getAccountByIdResult?.buildings?.forEach((building: BuildingProps) => {
          if (building?.organizations?.find((org: { uuid: string }) => org.uuid === orgWatch)) {
            methods.setValue('buildingUuid', building.uuid);
          }
          return;
        });
      }
    }
  }, [getAccountByIdResult, buildingWatch, orgWatch, methods]);

  const roleWatch = methods.watch('role');

  /* Ensure createQuery and updateQuery is defined or use a dummy function if not */
  const createQueryFunction = createQuery || (() => Promise.reject('createQuery is not defined'));
  const updateQueryFunction = updateQuery || (() => Promise.reject('updateQuery is not defined'));

  /* The code is creating a mutation function called `saveAdminMutation` using the `useSaveAdmin` hook. This hook takes in 
  several parameters: */
  const saveAdminMutation = useSaveAdmin({
    createQuery: createQueryFunction,
    updateQuery: updateQueryFunction,
    queryName,
    onSuccess: () => {
      setShowModal(false);
      setDisabled(false);
      if (!editUUID) {
        toast.success(generateSaveSuccessMessage('Admin'));
      } else {
        toast.success(generateUpdateSuccessMessage('Admin'));
      }
      if (setEditUUID) setEditUUID('');
    },
    onError: (error: any) => {
      setDisabled(false);
      if (error.response) {
        const errorMessage =
          error.response.data?.error ||
          error.response.data?.message ||
          'An unexpected error occurred.';

        switch (error.response.status) {
          case 409: // Conflict, user with the provided email already exists
            toast.error(errorMessage, { autoClose: 15000 });
            break;
          default:
            if (!editUUID) {
              toast.error(generateSaveErrorMessage('Admin'));
            } else {
              toast.error(generateUpdateErrorMessage('Admin'));
            }
            break;
        }
      } else {
        // Handle cases where the error is not from the server response
        toast.error(GetNonServerErrorMessage);
      }
    }
  });

  /* The `onSubmit` function takes an `AdminProps` object as an argument, extracts specific properties from it, and passes them to the 
  `saveAdminMutation.mutate` function. Based on the role of the user the appropriate `saveAdminMutation.mutate` function will be called. */
  const onSubmit = async (e: AdminProps) => {
    setDisabled(true);

    if (!e.accountUuid && !e.buildingUuid && !e.organizationUuid) {
      saveAdminMutation.mutate({
        uuid: e?.uuid,
        username: e.username,
        password: e.password,
        role: e.role
      });
    } else if (!e.buildingUuid && !e.organizationUuid) {
      saveAdminMutation.mutate({
        uuid: e?.uuid,
        username: e.username,
        password: e.password,
        role: e.role,
        accountUuid: e?.accountUuid
      });
    } else if (!e.organizationUuid) {
      saveAdminMutation.mutate({
        uuid: e?.uuid,
        username: e.username,
        password: e.password,
        role: e.role,
        accountUuid: e?.accountUuid,
        buildingUuid: e?.buildingUuid
      });
    } else {
      saveAdminMutation.mutate({
        uuid: e?.uuid,
        username: e.username,
        password: e.password,
        role: e.role,
        accountUuid: e?.accountUuid,
        buildingUuid: e?.buildingUuid,
        organizationUuid: e?.organizationUuid
      });
    }
  };

  const roleOptions =
    user.role === 'ACCOUNT'
      ? AdminRoleOptions.filter((option) => option.value !== 'SUPER')
      : AdminRoleOptions;

  return !editUUID || isSuccess ? (
    <>
      <Modal
        isOpen={showModal}
        onRequestClose={() => {
          setShowModal(false);
          if (setEditUUID) setEditUUID('');
        }}
        title={entityName}
      >
        <form onSubmit={methods.handleSubmit(onSubmit)} className={`text-left items-start`}>
          <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4 mr-8'}>
            <Input
              label="Username"
              name="username"
              type="text"
              placeholder="sam.smith@b-line.io"
              autoFocus
              methods={methods}
            />
            <Input
              label="Password"
              name="password"
              type="password"
              placeholder="******"
              autoFocus
              methods={methods}
              autoComplete="new-password"
            />
            <SelectInput
              Controller={Controller}
              control={methods.control}
              label="Role"
              name="role"
              defaultValue={getAdminResult?.role}
              errors={methods.formState.errors}
              placeholder="Select a role"
              options={roleOptions}
            />
            <AdminRoleFields
              role={roleWatch}
              Controller={Controller}
              control={methods.control}
              methods={methods}
              buildingOptions={buildingOptions}
              orgOptions={orgOptions}
              userRole={user.role}
            />
          </div>
          <PrimaryButton
            buttonLabel={entityName}
            iconLeft={<UsersIcon />}
            variant="default"
            size={'mt-4 px-6 w-auto gap-2'}
            disabled={disabled}
          />
        </form>
      </Modal>
    </>
  ) : (
    <LoadingSpinner />
  );
}
